// Working legal-copy state for one activation: both packs, documents, tokens.
//
// Territory on the activation picks which pack is published. The other pack
// stays here so a flip does not wipe it. Tokens fill the pack templates.
// A rewritten sentence is stored as bodyOverride and is not reassembled, except
// for the document URLs: those still follow the Names and URLs tokens, so a
// later change to Official rules URL updates `[official rules](…)` in a tick
// that was otherwise left alone.

import {
  ALL_TOKENS,
  CUSTOM_ACK_TEMPLATES,
  DEFAULT_MIN_AGE,
  DEFAULT_RETENTION_MONTHS,
  MAXIMUM_CUSTOM_ACKS,
  NOTICE_TEXT,
  OPTIONAL_DIVIDER,
  PRESET_BLOCKS,
  assemble,
  assembleForPreview,
  syncPackUrlsInText,
  blocksFor,
  defaultTokens,
  missingTokens,
  templatesFor,
  type PackBlockDef,
  type PackPreset,
  type PackToken,
  type Territory,
} from "../../../schema/src/consentPack.js";
import { blankLegalDrafts, type LegalDraft } from "./legalDraft.js";

export type PrivacyMode = "required_tick" | "notice_only";
export type TermsPrivacy = "combined" | "split";

export type BlockState = {
  readonly enabled: boolean;
  readonly bodyOverride: string | null;
  /** Title above the checkbox on the registration page. Null when there is none. */
  readonly heading: string | null;
};

export type CustomAck = {
  readonly id: string;
  readonly required: boolean;
  readonly body: string;
  readonly heading: string;
};

export type TerritoryPack = {
  readonly preset: PackPreset | null;
  readonly privacyMode: PrivacyMode;
  readonly termsPrivacy: TermsPrivacy;
  readonly prizeMechanic: boolean;
  readonly legalApprover: string;
  readonly tokens: Record<PackToken, string>;
  readonly blocks: Record<string, BlockState>;
  readonly custom: readonly CustomAck[];
};

export type LegalSetup = {
  readonly version: 1;
  readonly documents: readonly LegalDraft[];
  readonly packs: { readonly UK: TerritoryPack; readonly US: TerritoryPack };
  /** The Add-activation starter, when one was picked. Used to prefill agreements. */
  readonly showTemplate: string | null;
};

export function blankPack(territory: Territory, applyEnquiry: boolean): TerritoryPack {
  const tokens = defaultTokens();
  const blocks: Record<string, BlockState> = {};
  for (const block of blocksFor(territory)) {
    blocks[block.id] = { enabled: false, bodyOverride: null, heading: null };
  }
  const pack: TerritoryPack = {
    preset: applyEnquiry ? "enquiry" : null,
    privacyMode: "notice_only",
    termsPrivacy: "combined",
    prizeMechanic: false,
    legalApprover: "",
    tokens,
    blocks,
    custom: [],
  };
  return applyEnquiry ? applyPreset(pack, territory, "enquiry") : pack;
}

export function blankLegalSetup(applyEnquiry: boolean): LegalSetup {
  return {
    version: 1,
    documents: blankLegalDrafts(),
    packs: {
      UK: blankPack("UK", applyEnquiry),
      US: blankPack("US", false),
    },
    showTemplate: null,
  };
}

export function parseLegalSetup(value: unknown, applyEnquiryIfEmpty: boolean): LegalSetup {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return blankLegalSetup(applyEnquiryIfEmpty);
  }
  const body = value as Record<string, unknown>;
  if (body.version !== 1 || body.packs === null || typeof body.packs !== "object") {
    return blankLegalSetup(applyEnquiryIfEmpty);
  }
  const packs = body.packs as Record<string, unknown>;
  const documents = Array.isArray(body.documents)
    ? mergeDocuments(body.documents)
    : blankLegalDrafts();
  return {
    version: 1,
    documents,
    packs: {
      UK: parsePack(packs.UK, "UK", applyEnquiryIfEmpty),
      US: parsePack(packs.US, "US", false),
    },
    showTemplate: parseShowTemplate(body.showTemplate),
  };
}

function parseShowTemplate(value: unknown): string | null {
  return value === "promo_game" || value === "lead_capture" || value === "onsite_hall" ? value : null;
}

function parsePack(value: unknown, territory: Territory, applyEnquiry: boolean): TerritoryPack {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return blankPack(territory, applyEnquiry);
  }
  const body = value as Record<string, unknown>;
  const base = blankPack(territory, false);
  const tokens = { ...base.tokens };
  if (body.tokens !== null && typeof body.tokens === "object" && !Array.isArray(body.tokens)) {
    for (const name of ALL_TOKENS) {
      const raw = (body.tokens as Record<string, unknown>)[name];
      if (typeof raw === "string") tokens[name] = raw;
    }
  }
  const blocks = { ...base.blocks };
  if (body.blocks !== null && typeof body.blocks === "object" && !Array.isArray(body.blocks)) {
    for (const [id, state] of Object.entries(body.blocks as Record<string, unknown>)) {
      if (state === null || typeof state !== "object" || Array.isArray(state)) continue;
      const row = state as Record<string, unknown>;
      blocks[id] = {
        enabled: row.enabled === true,
        bodyOverride: typeof row.bodyOverride === "string" && row.bodyOverride.trim() !== "" ? row.bodyOverride : null,
        heading: typeof row.heading === "string" && row.heading.trim() !== "" ? row.heading.trim() : null,
      };
    }
  }
  const custom: CustomAck[] = [];
  if (Array.isArray(body.custom)) {
    for (const row of body.custom) {
      if (row === null || typeof row !== "object" || Array.isArray(row)) continue;
      const item = row as Record<string, unknown>;
      if (typeof item.id !== "string" || typeof item.body !== "string") continue;
      custom.push({
        id: item.id,
        required: item.required === true,
        body: item.body,
        heading: typeof item.heading === "string" ? item.heading : "",
      });
      if (custom.length === MAXIMUM_CUSTOM_ACKS) break;
    }
  }
  const preset =
    typeof body.preset === "string" && (PRESET_BLOCKS as Record<string, unknown>)[body.preset] !== undefined
      ? (body.preset as PackPreset)
      : null;
  return {
    preset,
    privacyMode: body.privacyMode === "required_tick" ? "required_tick" : "notice_only",
    termsPrivacy: body.termsPrivacy === "split" ? "split" : "combined",
    prizeMechanic: body.prizeMechanic === true,
    legalApprover: typeof body.legalApprover === "string" ? body.legalApprover : "",
    tokens,
    blocks,
    custom,
  };
}

function mergeDocuments(raw: readonly unknown[]): readonly LegalDraft[] {
  const blanks = blankLegalDrafts();
  return blanks.map((blank) => {
    const found = raw.find((row) => {
      if (row === null || typeof row !== "object" || Array.isArray(row)) return false;
      return (row as Record<string, unknown>).kind === blank.kind;
    }) as Record<string, unknown> | undefined;
    if (found === undefined) return blank;
    return {
      kind: blank.kind,
      enabled: found.enabled === true,
      title: typeof found.title === "string" ? found.title : blank.title,
      href: typeof found.href === "string" ? found.href : "",
      body: typeof found.body === "string" ? found.body : "",
      version: typeof found.version === "string" && found.version.trim() !== "" ? found.version : "1",
    };
  });
}

export function applyPreset(pack: TerritoryPack, territory: Territory, preset: PackPreset): TerritoryPack {
  const spec = PRESET_BLOCKS[preset];
  const blocks: Record<string, BlockState> = {};
  for (const block of visibleBlocks(territory, pack.termsPrivacy, pack.privacyMode)) {
    const on = spec.required.includes(block.number) || spec.optional.includes(block.number);
    const previous = pack.blocks[block.id];
    blocks[block.id] = {
      enabled: on,
      bodyOverride: previous?.bodyOverride ?? null,
      heading: previous?.heading ?? null,
    };
  }
  for (const [id, state] of Object.entries(pack.blocks)) {
    if (blocks[id] === undefined) {
      blocks[id] = { enabled: false, bodyOverride: state.bodyOverride, heading: state.heading ?? null };
    }
  }
  return {
    ...pack,
    preset,
    prizeMechanic: preset === "prize" || preset === "game",
    blocks,
  };
}

export function visibleBlocks(
  territory: Territory,
  termsPrivacy: TermsPrivacy,
  privacyMode: PrivacyMode,
): readonly PackBlockDef[] {
  return blocksFor(territory).filter((block) => {
    if (territory === "US") {
      if (termsPrivacy === "combined" && (block.id === "US-02-terms" || block.id === "US-03")) return false;
      if (termsPrivacy === "split" && block.id === "US-02") return false;
    }
    if (territory === "UK" && block.id === "UK-03" && privacyMode === "notice_only") return false;
    return true;
  });
}

export function patchPack(setup: LegalSetup, territory: Territory, next: TerritoryPack): LegalSetup {
  return { ...setup, packs: { ...setup.packs, [territory]: next } };
}

export function setToken(pack: TerritoryPack, name: PackToken, value: string): TerritoryPack {
  const tokens = { ...pack.tokens, [name]: value };
  const blocks: Record<string, BlockState> = {};
  for (const [id, state] of Object.entries(pack.blocks)) {
    if (state.bodyOverride === null) {
      blocks[id] = state;
      continue;
    }
    const synced = syncPackUrlsInText(state.bodyOverride, tokens);
    blocks[id] = synced === state.bodyOverride ? state : { ...state, bodyOverride: synced };
  }
  const custom = pack.custom.map((row) => {
    const synced = syncPackUrlsInText(row.body, tokens);
    return synced === row.body ? row : { ...row, body: synced };
  });
  return { ...pack, tokens, blocks, custom };
}

export function setBlock(pack: TerritoryPack, id: string, next: Partial<BlockState>): TerritoryPack {
  const current = pack.blocks[id] ?? { enabled: false, bodyOverride: null, heading: null };
  return { ...pack, blocks: { ...pack.blocks, [id]: { ...current, ...next } } };
}

export function assembledBody(block: PackBlockDef, pack: TerritoryPack): string {
  return fillBody(block, pack, assemble);
}

/** Same sentences as assembledBody, with unfilled holes named for View Live. */
export function previewBody(block: PackBlockDef, pack: TerritoryPack): string {
  return fillBody(block, pack, assembleForPreview);
}

/** The pack sentence with the current tokens, ignoring any rewrite. */
export function assembledFromTemplates(block: PackBlockDef, pack: TerritoryPack): string {
  return fillFromTemplates(block, pack, assemble);
}

function fillFromTemplates(
  block: PackBlockDef,
  pack: TerritoryPack,
  fill: (template: string, tokens: Readonly<Record<string, string>>) => string,
): string {
  const tokens = { ...pack.tokens };
  const source = templatesFor(block, pack.preset);
  let template = source.template;
  if (
    source.subtextTemplate !== undefined &&
    (block.id === "UK-01" || block.id === "US-01" || pack.prizeMechanic)
  ) {
    const sub = fill(source.subtextTemplate, tokens);
    template = sub === "" ? source.template : `${source.template}\n\n${source.subtextTemplate}`;
  }
  return fill(template, tokens);
}

function fillBody(
  block: PackBlockDef,
  pack: TerritoryPack,
  fill: (template: string, tokens: Readonly<Record<string, string>>) => string,
): string {
  const override = pack.blocks[block.id]?.bodyOverride;
  if (override !== null && override !== undefined) {
    return syncPackUrlsInText(override, pack.tokens);
  }
  return fillFromTemplates(block, pack, fill);
}

export function noticeText(territory: Territory, pack: TerritoryPack): string {
  return assemble(NOTICE_TEXT[territory], pack.tokens);
}

export function previewNotice(territory: Territory, pack: TerritoryPack): string {
  return assembleForPreview(NOTICE_TEXT[territory], pack.tokens);
}

export function optionalDivider(territory: Territory): string {
  return OPTIONAL_DIVIDER[territory];
}

export type CompiledTick = {
  readonly id: string;
  readonly title: string;
  readonly role: "attestation" | "consent";
  readonly required: boolean;
  readonly body: string;
  readonly heading?: string;
  readonly purpose?: PackBlockDef["purpose"];
  readonly attestationKind?: PackBlockDef["attestationKind"];
  readonly needsTcpa?: boolean;
};

export function compileTicks(territory: Territory, pack: TerritoryPack): readonly CompiledTick[] {
  return collectTicks(territory, pack, "publish");
}

/** The ticks View Live and the starting-point preview draw, holes named. */
export function compilePreviewTicks(territory: Territory, pack: TerritoryPack): readonly CompiledTick[] {
  return collectTicks(territory, pack, "preview");
}

function collectTicks(
  territory: Territory,
  pack: TerritoryPack,
  mode: "publish" | "preview",
): readonly CompiledTick[] {
  const bodyOf = mode === "preview" ? previewBody : assembledBody;
  const fill = mode === "preview" ? assembleForPreview : assemble;
  const ticks: CompiledTick[] = [];
  for (const block of visibleBlocks(territory, pack.termsPrivacy, pack.privacyMode)) {
    if (pack.blocks[block.id]?.enabled !== true) continue;
    const heading = pack.blocks[block.id]?.heading?.trim() ?? "";
    ticks.push({
      id: block.id,
      title: block.title,
      role: block.role,
      required: block.role === "attestation",
      body: bodyOf(block, pack),
      ...(heading === "" ? {} : { heading }),
      ...(block.purpose === undefined ? {} : { purpose: block.purpose }),
      ...(block.attestationKind === undefined ? {} : { attestationKind: block.attestationKind }),
      ...(block.needsTcpa === undefined ? {} : { needsTcpa: block.needsTcpa }),
    });
  }
  for (const custom of pack.custom) {
    if (custom.body.trim() === "") continue;
    const heading = custom.heading.trim();
    ticks.push({
      id: custom.id,
      title: custom.required ? "Custom acknowledgement" : "Custom acknowledgement (optional)",
      role: custom.required ? "attestation" : "consent",
      required: custom.required,
      body: syncPackUrlsInText(fill(custom.body, pack.tokens), pack.tokens),
      ...(heading === "" ? {} : { heading }),
      ...(custom.required ? { attestationKind: "terms_acceptance" as const } : { purpose: "enquiry_follow_up" as const }),
    });
  }
  return ticks;
}

export function legalSetupProblems(territory: Territory, setup: LegalSetup): readonly string[] {
  const pack = setup.packs[territory];
  const ticks = compileTicks(territory, pack);
  const problems: string[] = [];
  if (ticks.filter((tick) => tick.role === "attestation").length === 0) {
    problems.push("At least one required tick must be on before Publish Live");
  }
  const purposes = ticks
    .filter((tick) => tick.role === "consent")
    .map((tick) => tick.purpose)
    .filter((purpose): purpose is NonNullable<typeof purpose> => purpose !== undefined);
  if (new Set(purposes).size !== purposes.length) {
    problems.push("Two optional ticks cannot share one purpose");
  }
  for (const tick of ticks) {
    const block = visibleBlocks(territory, pack.termsPrivacy, pack.privacyMode).find((row) => row.id === tick.id);
    if (block !== undefined) {
      const source = templatesFor(block, pack.preset);
      const missing = missingTokens(
        pack.blocks[block.id]?.bodyOverride ?? `${source.template}\n${source.subtextTemplate ?? ""}`,
        pack.tokens,
      );
      if (missing.length > 0) {
        problems.push(`${block.title}: fill ${missing.join(", ")}`);
      }
    }
    if (tick.body.trim() === "") problems.push(`${tick.title}: needs wording`);
    if (tick.needsTcpa === true) {
      if (pack.tokens.TCPA_TEXT.trim() === "") {
        problems.push(`${tick.title}: client TCPA text is required`);
      }
      if (pack.legalApprover.trim() === "") {
        problems.push(`${tick.title}: a named legal approver is required`);
      }
    }
  }
  if (pack.custom.some((row) => row.body.trim() !== "") && pack.legalApprover.trim() === "") {
    problems.push("A named legal approver is required before a custom acknowledgement can be published");
  }
  if (pack.custom.length > MAXIMUM_CUSTOM_ACKS) {
    problems.push(`At most ${MAXIMUM_CUSTOM_ACKS} custom acknowledgements`);
  }
  return problems;
}

export function customTemplate(index: number): string {
  return CUSTOM_ACK_TEMPLATES[index] ?? CUSTOM_ACK_TEMPLATES[0] ?? "";
}

export { DEFAULT_MIN_AGE, DEFAULT_RETENTION_MONTHS };
